// CIN-6288: shared constants for the Task Manager list, board, and modals.
import moment from 'moment-timezone';

// Read-only "Type" column / chip labels, keyed by the task's `source_type`.
// Text message has no inclusion FK (the SMS inbox is a per-customer conversation,
// so this discriminator alone surfaces it); review/survey/voice_call/schedule each
// link their source record.
export const SOURCE_TYPE_LABELS = {
  manual: 'Manual',
  review: 'Review',
  survey: 'Survey',
  voice_call: 'Voice call',
  text_message: 'Text message',
  schedule: 'Schedule',
  journey: 'Journey'
};

// Persisted in the URL so the table/board toggle survives reloads and Back.
export const VIEW_TABLE = 'table';
export const VIEW_BOARD = 'board';

// CIN-7070: the "All boards" choice (table only). Also the value widget deep links
// send (`?board=all`) so a user's remembered board can never narrow a widget click.
export const ALL_BOARDS = 'all';

// CIN-7070: the table/kanban view (one global preference) and the last board (per
// company, so each company reopens on its own board) are remembered per browser.
// Only a click writes them — a ?view= or ?board= link applies to that visit without
// changing the stored preference.
const VIEW_STORAGE_KEY = 'taskManager.view';
const boardStorageKey = companyId => `taskManager.board.${companyId}`;

// localStorage can throw (privacy modes, full quota); a missing preference is fine.
const readStorage = key => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Not persisting a preference is harmless.
  }
};
export const getStoredView = () => {
  const view = readStorage(VIEW_STORAGE_KEY);
  return view === VIEW_BOARD || view === VIEW_TABLE ? view : null;
};
export const storeView = view => writeStorage(VIEW_STORAGE_KEY, view);
export const getStoredBoard = companyId => companyId ? readStorage(boardStorageKey(companyId)) : null;
export const storeBoard = (companyId, boardId) => companyId && writeStorage(boardStorageKey(companyId), boardId);

// CIN-6288: curated stage colors. Stages store a stable `color` KEY (not a raw
// hex) so we own the palette — a small set of pastel chip pairs that read well
// together and keep enough contrast for the dark label text. The board column
// header, the list Stage chip, and the board card resolve a stored key to its
// { bg, text } pair via getStageColor(); an unset/unknown color falls back to
// the neutral default (first entry).
export const STAGE_COLORS = [{
  key: 'slate',
  label: 'Slate',
  bg: '#EDF0F4',
  text: '#48566B'
}, {
  key: 'blue',
  label: 'Blue',
  bg: '#DAEFFF',
  text: '#1C6DA4'
}, {
  key: 'teal',
  label: 'Teal',
  bg: '#D5F1EC',
  text: '#0F766E'
}, {
  key: 'green',
  label: 'Green',
  bg: '#E3F4EA',
  text: '#0A8039'
}, {
  key: 'amber',
  label: 'Amber',
  bg: '#FBEFD3',
  text: '#9A6700'
}, {
  key: 'orange',
  label: 'Orange',
  bg: '#FCE6D6',
  text: '#B5470B'
}, {
  key: 'red',
  label: 'Red',
  bg: '#FCE2E2',
  text: '#B42318'
}, {
  key: 'purple',
  label: 'Purple',
  bg: '#ECE6FB',
  text: '#6941C6'
}, {
  key: 'pink',
  label: 'Pink',
  bg: '#FCE4F1',
  text: '#B0468A'
}];
export const DEFAULT_STAGE_COLOR_KEY = 'slate';

// Resolve a stored stage color key to its chip { bg, text }. Falls back to the
// neutral default so legacy stages (color = null) and any unknown value render
// cleanly rather than blank.
export const getStageColor = key => STAGE_COLORS.find(c => c.key === key) || STAGE_COLORS[0];

// CIN-7073: the list "Due" filter (backed by the server's `due_status` token filter)
// and the dashboard deep links share these values. "Any" is the radio default,
// which clears the filter rather than sending a token.
export const DUE_STATUS_OVERDUE = 'overdue';
export const DUE_STATUS_TODAY = 'today';
export const DUE_STATUS_WEEK = 'week';
export const DUE_STATUS_NONE = 'none';
export const DUE_STATUS_FILTER_OPTIONS = [{
  label: 'Any',
  value: '',
  isDefault: true
}, {
  label: 'Overdue',
  value: DUE_STATUS_OVERDUE
}, {
  label: 'Due today',
  value: DUE_STATUS_TODAY
}, {
  label: 'Due in 7 days',
  value: DUE_STATUS_WEEK
}, {
  label: 'No due date',
  value: DUE_STATUS_NONE
}];

// CIN-7073: overdue reuses the curated red pair so it reads as the same "alarm"
// color everywhere (list chip, board card edge, detail pill, widget tile).
export const OVERDUE_COLOR = getStageColor('red');
export const DUE_TODAY_COLOR = getStageColor('orange');

// CIN-7073: "today" in the COMPANY's timezone, as 'YYYY-MM-DD' — the calendar day
// the server uses for is_overdue and the due filters. An unknown zone doesn't throw
// in moment-timezone (it logs and falls back to browser-local time), so it is
// checked first and replaced by the server's own fallback zone.
export const companyTodayISO = companyTimezone => {
  const zone = companyTimezone && moment.tz.zone(companyTimezone) ? companyTimezone : 'America/Denver';
  return moment().tz(zone).format('YYYY-MM-DD');
};

// CIN-7073: 'Sep 30' in the current year, 'Sep 30, 2027' otherwise. Parsed as a
// plain calendar date so no timezone can shift it by a day.
export const formatDueDate = dueDate => {
  if (!dueDate) return '';
  const day = moment(dueDate, 'YYYY-MM-DD');
  return day.format(day.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY');
};

// CIN-7073: the due-state reading behind DueChip (list, board, and offered to the
// drawer). `is_overdue` comes from the server (company-local) and is checked first,
// so a chip can't contradict the server's overdue verdict.
export const getDueState = (task, companyTimezone) => {
  if (!task?.due_date) return 'none';
  if (task.completed_when) return 'done';
  if (task.is_overdue) return 'overdue';
  if (task.due_date === companyTodayISO(companyTimezone)) return 'today';
  return 'upcoming';
};

// CIN-6288 (R5 #3): a Catalyst can be assigned a task scoped to a location they
// can't otherwise access — the API still returns it because it's assigned to
// them. They can see and act on the card, but not the location's other data, so
// the UI warns them. Returns true when that mismatch applies to `user`.
export const isAssignedOutsideLocationAccess = (task, {
  isCatalystUser,
  userLocations = [],
  user
} = {}) => {
  if (!isCatalystUser || !task || !task.visibility_location) return false;
  const assignedToMe = String(task.assigned_to) === String(user?.id);
  if (!assignedToMe) return false;
  return !(userLocations || []).map(String).includes(String(task.visibility_location));
};

// CIN-6288 (R5): warning shown when an assigned Catalyst lacks access to the
// task's location. Shared so the detail-modal Alert and the board-card tooltip
// stay in sync.
export const LOCATION_ACCESS_WARNING = "You're assigned this task, but it's scoped to a location you don't have access to. You can view and update the task itself, but not the location's other data. Contact your company's Admin in Cinch to determine if you need more access.";